import classnames from 'classnames2';
import { kebabCase } from 'lodash-es';
import React from 'react';

import { ITestProps } from '../../utils/dataTest';

import { Icon } from '../../Icon';
import { Tooltip } from '../../Tooltip';
import { SelectList } from '../Select.List';
import { SelectContextWrapper } from '../SelectContextWrapper';
import { SelectIsEqual } from '../types';
import { EChildMenuDirections } from '../useControls';
import { useSelectContext } from '../useSelectContext';

import styles from './Select.Option.module.scss';

export interface ISelectOptionProps extends ITestProps {
  /** Whether the option should be disabled */
  disabled?: boolean;
  /** The text to be displayed for this option on the options menu. */
  label: string;
  /** A secondary label to display to the right of the (primary) label. */
  secondaryLabel?: string;
  /** The icon component to display to the left of the menu option. */
  prefixIcon?: React.ReactNode;
  /** Internal usage. Used by Select to apply the appropriate style for selected options. */
  selected?: boolean;
  focused?: boolean;
  /** The form value associated with this option. It is passed to the onChange callback on Select. */
  value: any;
  /** A description to display below the line containing the primary (and secondary) label(s).
   * It is left-aligned with the label above it (noticeabled if there is a prefixIcon),
   * causes the (primary) label to be bolded and increases the spacing between this option
   * and the options above and below it.
   */
  description?: string;
  /** The indentation level of this option. Should be a number between 0 and 4. */
  indentLevel?: number;
  parent?: any;
  tooltip?: string;
  /** Allow for a custom isEqual function. */
  isEqual?: SelectIsEqual;
}

function defaultIsEqual(a, b) {
  return a && b && a.equals ? a.equals(b) : a === b;
}

const SelectOptionTriggerButton = (props: ISelectOptionProps) => {
  const { isOpen, popper, buttonRef, onOpen, onClose, hasParentMenu, childDirection } = useSelectContext();

  const optionClasses = classnames(styles.option, {
    [styles.optionSelected]: props.selected,
    [styles.optionDisabled]: props.disabled,
    [styles.descriptionPresent]: props.description,
    [styles.highlighted]: isOpen
  });

  const openAndFocusMenu = () => {
    onOpen();
    if (popper.ref.current) {
      popper.ref.current.focus();
    }
  };

  const handleMouseEnter = () => {
    if (!hasParentMenu) return;
    onOpen();
  };

  const handleMouseLeave = (e) => {
    // if the next target is the parent its probably a scroll bar
    if (e.relatedTarget === e.currentTarget.parentElement) return;
    // if the next target is our list, we don't want to close
    if (e.relatedTarget === e.currentTarget.nextSibling) return;
    onClose();
  };

  const handleClick = () => {
    onOpen();
  };

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        openAndFocusMenu();
      } else if (event.key === 'ArrowDown') {
        if (!hasParentMenu) {
          onOpen();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (hasParentMenu) {
          onOpen();
        }
      }
    },
    [hasParentMenu]
  );

  return (
    <button
      role="option"
      aria-label={`${props.label}, Option Group, arrow right to open`}
      aria-selected={props.selected ? 'true' : 'false'}
      ref={buttonRef}
      className={classnames(styles.selectOptionButton, {
        [styles.disabled]: props.disabled
      })}
      data-test={props.dataTest}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={props.disabled}
      type="button"
    >
      <div
        className={classnames(optionClasses, styles[`indentation${props.indentLevel}`])}
        data-test={`select-option-text-${kebabCase(props.label)}`}
      >
        {childDirection === EChildMenuDirections.Left && (
          <Icon icon="arrow-caret-down" className={styles.caretLeft} dataTest="left-arrow-icon" />
        )}

        {props.prefixIcon && <div className={styles.iconWrapper}>{props.prefixIcon}</div>}
        <div
          className={classnames(styles.primary, styles.primaryChild)}
          data-test={`select-option-primary-text-${kebabCase(props.label)}`}
        >
          {props.label}
        </div>

        {props.secondaryLabel ? (
          <div
            className={classnames(styles.secondary, { [styles.optionDisabled]: props.disabled })}
            data-test={`select-option-secondary-text-${kebabCase(props.secondaryLabel)}`}
          >
            {props.secondaryLabel}
          </div>
        ) : null}

        {childDirection !== EChildMenuDirections.Left && (
          <Icon icon="arrow-caret-down" className={styles.caretRight} dataTest="right-arrow-icon" />
        )}
      </div>
    </button>
  );
};

const SelectOptionButton: React.FC<ISelectOptionProps> = (props) => {
  const { disabled, label, isEqual = defaultIsEqual } = props;
  const dataTest = props.dataTest ?? kebabCase(props.label);
  const { selectedValue, onChange, onCloseAll } = useSelectContext();

  const isSelected = props.value && isEqual(selectedValue, props.value);

  const optionClasses = classnames(styles.option, {
    [styles.optionSelected]: isSelected,
    [styles.optionDisabled]: disabled,
    [styles.descriptionPresent]: props.description
  });

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      event.preventDefault();

      if (onChange) {
        onChange(props.value, event, props.parent);
      }

      onCloseAll();
    }
  };

  const handleClick = (event) => {
    changeHandler(event);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      changeHandler(event);
    }
  };

  const optionButton = (
    <button
      role="option"
      aria-selected={isSelected ? 'true' : 'false'}
      className={classnames(styles.selectOptionButton, {
        [styles.disabled]: disabled
      })}
      data-test={dataTest}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      type="button"
    >
      <div
        className={classnames(optionClasses, styles[`indentation${props.indentLevel}`])}
        data-test={`select-option-text-${dataTest}`}
      >
        {props.prefixIcon && <div className={styles.iconWrapper}>{props.prefixIcon}</div>}
        <div className={styles.labelsContainer}>
          <div className={styles.flexContainer}>
            <div className={styles.primary} data-test={`select-option-primary-text-${dataTest}`}>
              {label}
            </div>

            {props.secondaryLabel ? (
              <div
                className={classnames(styles.secondary, { [styles.optionDisabled]: disabled })}
                data-test={`select-option-secondary-text-${dataTest}`}
              >
                {props.secondaryLabel}
              </div>
            ) : null}
          </div>

          {props.description ? (
            <div className={styles.description} data-test={`select-option-description-text-${dataTest}`}>
              {props.description}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
  return props.tooltip ? <Tooltip content={props.tooltip}>{optionButton}</Tooltip> : optionButton;
};

export const SelectOption: React.FC<ISelectOptionProps> = (props) => {
  const { isEqual = defaultIsEqual } = props;
  const { onChange, selectedValue, childDirection } = useSelectContext();
  let hasSelection = false;

  if (props.children) {
    React.Children.toArray(props.children).forEach((child) => {
      if (isEqual((child as any).props.value, selectedValue)) {
        hasSelection = true;
      }
    });
  } else {
    hasSelection = isEqual(props.value, selectedValue);
  }

  return props.children ? (
    <SelectContextWrapper onChange={onChange} selectedValue={selectedValue} childDirection={childDirection}>
      <SelectOptionTriggerButton {...props} selected={hasSelection} />
      <SelectList>{props.children}</SelectList>
    </SelectContextWrapper>
  ) : (
    <SelectOptionButton {...props} />
  );
};

SelectOption.displayName = 'Select.Option';
